'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  Cookie,
  X,
  Settings,
  Shield,
  AlertCircle,
  ChevronDown,
  Check,
} from 'lucide-react';
import Link from 'next/link';

interface CookieConsentProps {
  tenantId?: string;
}

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const COOKIE_CATEGORIES = [
  {
    id: 'necessary' as const,
    title: 'Strictly Necessary',
    description: 'Required for core site functionality.',
    required: true,
    icon: Shield,
  },
  {
    id: 'analytics' as const,
    title: 'Analytics',
    description: 'Helps us improve performance and usability.',
    required: false,
    icon: Cookie,
  },
  {
    id: 'marketing' as const,
    title: 'Marketing',
    description: 'Used for relevant ads and campaigns.',
    required: false,
    icon: AlertCircle,
  },
  {
    id: 'preferences' as const,
    title: 'Preferences',
    description: 'Remembers your settings and choices.',
    required: false,
    icon: Settings,
  },
];

export function CookieConsent({ tenantId }: CookieConsentProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  const storageKey = `cookie-consent-${tenantId || 'global'}`;

  /* ---------------- Init ---------------- */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) {
        setTimeout(() => setOpen(true), 1400);
      } else {
        setPrefs(JSON.parse(saved));
      }
    } catch {
      setOpen(true);
    }
  }, [storageKey]);

  /* ---------------- Persist ---------------- */
  const persist = useCallback(
    (data: CookiePreferences) => {
      localStorage.setItem(storageKey, JSON.stringify(data));
      localStorage.setItem('cookie-consent-at', new Date().toISOString());
    },
    [storageKey]
  );

  /* ---------------- Actions ---------------- */
  const acceptAll = () => {
    const all = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    persist(all);
    setOpen(false);
  };

  const rejectAll = () => {
    const minimal = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    persist(minimal);
    setOpen(false);
  };

  const acceptSelected = () => {
    persist(prefs);
    setOpen(false);
  };

  const toggle = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  if (!open) return null;

  return (
    < >
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setOpen(false)}
      />

      {/* Bottom Sheet */}
      <motion.div
        className="fixed inset-x-0 bottom-0 z-[101]"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        role="dialog"
        aria-modal="true"
      >
        <div className="relative mx-auto max-w-6xl bg-white rounded-t-3xl shadow-[0_-12px_40px_rgba(0,0,0,0.18)] border-t">

          {/* Close */}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="absolute -top-5 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white border shadow hover:bg-gray-100 hover:ring-2 hover:ring-blue-500/30 transition"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>

          <div className="px-4 sm:px-6 py-6">

            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Cookie className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                  Cookie Preferences
                </h2>
                <p className="mt-1 text-sm text-gray-600 max-w-xl">
                  Control how cookies are used to improve your experience.
                </p>
              </div>
            </div>

            {/* Expand */}
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex w-full items-center justify-between py-3 text-left group"
            >
              <span className="text-sm font-semibold text-gray-900">
                Manage categories
              </span>
              <span className="p-1.5 rounded-full group-hover:bg-gray-100 transition">
                <ChevronDown
                  className={`h-5 w-5 text-gray-600 transition-transform ${
                    expanded ? 'rotate-180' : ''
                  }`}
                />
              </span>
            </button>

            {/* Categories */}
            < >
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 space-y-3 max-h-[36vh] overflow-y-auto pr-1">
                    {COOKIE_CATEGORIES.map((c) => {
                      const Icon = c.icon;
                      const enabled = prefs[c.id];

                      return (
                        <div
                          key={c.id}
                          className="flex items-center justify-between gap-4 rounded-xl border p-4"
                        >
                          <div className="flex items-start gap-3">
                            <Icon className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {c.title}
                                {c.required && (
                                  <span className="ml-1 text-xs text-gray-500">
                                    (Required)
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {c.description}
                              </p>
                            </div>
                          </div>

                          {c.required ? (
                            <span className="text-xs px-3 py-1 rounded-full bg-gray-200 text-gray-700">
                              On
                            </span>
                          ) : (
                            <button
                              onClick={() => toggle(c.id)}
                              aria-pressed={enabled}
                              className={`relative h-6 w-11 rounded-full transition ${
                                enabled ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
                                  enabled
                                    ? 'translate-x-5'
                                    : 'translate-x-0.5'
                                }`}
                              >
                                {enabled && (
                                  <Check className="h-3 w-3 text-blue-600" />
                                )}
                              </span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </ >

            {/* Policy */}
            <div className="mt-4 text-center">
              <Link
                href={`/${tenantId ? `tenant/${tenantId}` : ''}/privacy`}
                className="inline-flex items-center text-xs font-medium text-blue-600 hover:underline"
              >
                <Shield className="h-4 w-4 mr-1" />
                Privacy & Cookie Policy
              </Link>
            </div>

            {/* Actions */}
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                onClick={rejectAll}
                className="flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Reject
              </button>

              <button
                onClick={acceptSelected}
                className="flex-1 rounded-lg bg-gray-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 flex items-center justify-center gap-1.5"
              >
                <Check className="h-4 w-4" />
                Accept Selected
              </button>

              <button
                onClick={acceptAll}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </ >
  );
}
